import React, { createContext, useContext } from 'react';
import usePassiveRedux from '@sift/resift/usePassiveRedux';
import useOrgChartSettingsFetch from './useOrgChartSettingsFetch';
import _get from 'lodash/get';
import * as Sift from '@sift/types';

// TODO note: revert these changes once harness no longer needed
// TODO Pearl: implement org chart settings
export type ResolvedOrgChartSettings = {
  [P in keyof Sift.OrgChartSettings]-?: Sift.OrgChartSettings[P];
};

const OrgChartSettingsOverrideContext = createContext<ResolvedOrgChartSettings | null>(null);

interface OrgChartSettingsOverrideProviderProps {
  children: React.ReactNode;
  settings: ResolvedOrgChartSettings;
}

/**
 * Supplies presentation settings to a bounded renderer without coupling that
 * renderer to the current-org-chart settings fetch.
 */
export function OrgChartSettingsOverrideProvider({
  children,
  settings,
}: OrgChartSettingsOverrideProviderProps) {
  return React.createElement(
    OrgChartSettingsOverrideContext.Provider,
    { value: settings },
    children,
  );
}

function useOrgChartSettingsOverride(): ResolvedOrgChartSettings | null {
  return useContext(OrgChartSettingsOverrideContext);
}

function useOrgChartSettings(): ResolvedOrgChartSettings {
  const overrideSettings = useOrgChartSettingsOverride();
  const config = usePassiveRedux((state: any) =>
    _get(state, 'configurations.user', {}),
  ) as Sift.Configuration;

  if (overrideSettings) {
    return overrideSettings;
  }

  if (_get(window, ['appState', 'orgChartSettings'])) {
    return _get(window, ['appState', 'orgChartSettings']);
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [orgChartSettingsFetchResult] = useOrgChartSettingsFetch();
  const orgChartSettings = orgChartSettingsFetchResult as Sift.OrgChartSettings;

  const orgChartHighlightBy = _get(config, 'layouts.org-chart-highlight-by') || [];
  const _highlightField = _get(orgChartSettings, 'highlightField', null) as string;
  const highlightField = (
    orgChartHighlightBy.map((item: any) => item.name).includes(_highlightField)
      ? _highlightField
      : null
  ) as string;

  return { sortBy: '', ...orgChartSettings, highlightField };
}

export default useOrgChartSettings;
